import React, { FC } from 'react'

import cn from 'classnames'
import { navigate } from 'gatsby'
import { Swiper, SwiperSlide } from 'swiper/react'
import 'swiper/css'
import { Swiper as SwiperClass, SwiperOptions } from 'swiper/types'

import classes from './smarty-tabs.module.scss'

const removeStyleAttr = (swiper: SwiperClass) => {
  if (!swiper.params.enabled) {
    swiper.wrapperEl.removeAttribute('style')
    swiper.slides.forEach((slide) => slide.removeAttribute('style'))
  }
}
const options: SwiperOptions = {
  spaceBetween: 16,
  breakpoints: {
    0: {
      enabled: true,
      slidesPerView: 'auto',
      centeredSlides: false,
    },
    760: {
      enabled: false,
    },
  },
}

interface SmartyTabsProps {
  tabs: {
    hash: string
    content: string
    itemName: string
  }[]
  // Which tab is selected, e.g. '#classic'. Passed down rather than
  // re-derived here from @reach/router's useLocation() — that hook doesn't
  // reliably reflect the real hash on initial hydration, so the pill could
  // highlight the wrong tab even once plan-batches.tsx was already showing
  // the right content from the same URL. The caller already has a correct,
  // hydration-safe value for this (see the `hasMounted` gate in
  // plan-batches.tsx), so this stays a single source of truth.
  activeHash: string
}

export const SmartyTabs: FC<SmartyTabsProps> = ({ tabs, activeHash }) => {
  // tab-hash-2-index, index-2-tab-hash mapping
  // used for smooth integration between slider, tabs, and URL
  const mapping = tabs.reduce(
    (acc, tab, i) => ({ ...acc, [tab.hash]: i, [i]: tab.hash }),
    {}
  )
  const activeIndex = activeHash in mapping ? mapping[activeHash] : 0
  const handleTabClick = async (index: number) => await navigate(mapping[index])

  return (
    <div className={classes.root} role="tablist">
      <Swiper
        runCallbacksOnInit={false}
        onInit={removeStyleAttr}
        onResize={removeStyleAttr}
        initialSlide={activeIndex}
        {...options}
      >
        {tabs.map((tab, index) => {
          return (
            <SwiperSlide key={tab.hash}>
              <button
                type="button"
                onClick={() => handleTabClick(index)}
                className={cn(
                  classes.pill,
                  mapping[activeIndex] === tab.hash && classes.pillActive
                )}
                data-item-category="GI"
                data-item-name={tab.itemName}
                role="tab"
              >
                {tab.content}
              </button>
            </SwiperSlide>
          )
        })}
      </Swiper>
    </div>
  )
}
